import { useMemo } from "react"
import { IDashboardContextValue } from "connected-components/dashboard/context/types"
import { DashboardAnalyticsContext } from "./types"
import { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { SelectedInstrument } from "helpers/types/instruments/selected"

// TODO: Make all of these types better
type SelectedInstrumentWithOptionalMetadata = InstrumentWithMetadata | SelectedInstrument | undefined | null

type InstrumentType = "ETF" | "BASKET" | "INDEX" | "BENCHMARK" | "PORTFOLIO"

const getInstrumentId = (instrument: SelectedInstrumentWithOptionalMetadata): string | undefined => {
  if (!instrument) return undefined
  return "alias" in instrument ? instrument.alias : instrument.id
}

const getInstrumentName = (instrument: SelectedInstrumentWithOptionalMetadata): string | undefined => {
  if (!instrument) return undefined
  if ("name" in instrument && typeof instrument.name === "string") return instrument.name
  if ("description" in instrument && typeof instrument.description === "string") return instrument.description
  return undefined
}

const getInstrumentType = (instrument: SelectedInstrumentWithOptionalMetadata): InstrumentType | undefined => {
  return instrument?.type as InstrumentType | undefined
}

/**
 * Custom hook to extract analytics properties from dashboard context with memoization.
 * Only recomputes when primitive analytics values change, not when context object reference changes.
 *
 * @param dashboardContext - The dashboard context value (must not be null)
 * @returns Memoized dashboard analytics properties (all optional based on dashboard state)
 */
export function useExtractDashboardContextProperties(
  dashboardContext: IDashboardContextValue,
): Partial<DashboardAnalyticsContext> {
  const {
    modelId,
    selectedMode,
    period,
    isIntraday,
    isEndVsStartDateComparison,
    instrumentsMetadata,
    instrument,
    comparisonInstrument,
    base,
    secondInstrument,
  } = dashboardContext

  const original = instrumentsMetadata?.original ?? instrument
  const instrumentId = getInstrumentId(original)
  const instrumentName = getInstrumentName(original)
  const instrumentType = getInstrumentType(original)

  const comparison = instrumentsMetadata?.comparison ?? comparisonInstrument
  const comparisonInstrumentId = getInstrumentId(comparison)
  const comparisonInstrumentName = getInstrumentName(comparison)
  const comparisonInstrumentType = getInstrumentType(comparison)

  const baseInstrument = instrumentsMetadata?.base ?? base
  const baseId = getInstrumentId(baseInstrument)
  const baseName = getInstrumentName(baseInstrument)
  const baseType = getInstrumentType(baseInstrument)

  const hasComparison = Boolean(comparison)
  const hasSecondInstrument = Boolean(secondInstrument)
  const hasBase = Boolean(baseInstrument)

  return useMemo(
    () => ({
      modelId,
      instrumentId,
      instrumentName,
      instrumentType,
      comparisonInstrumentId,
      comparisonInstrumentName,
      comparisonInstrumentType,
      baseId,
      baseName,
      baseType,
      selectedMode,
      hasComparison,
      hasSecondInstrument,
      hasBase,
      period,
      isIntraday,
      isEndVsStartDateComparison,
    }),
    [
      modelId,
      instrumentId,
      instrumentName,
      instrumentType,
      comparisonInstrumentId,
      comparisonInstrumentName,
      comparisonInstrumentType,
      baseId,
      baseName,
      baseType,
      selectedMode,
      hasComparison,
      hasSecondInstrument,
      hasBase,
      period,
      isIntraday,
      isEndVsStartDateComparison,
    ],
  )
}
